import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { nl, fr, es, enUS } from "date-fns/locale";
import { Check, Upload, X, Calendar as CalIcon, Plus, Minus, AlertCircle, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { runWithConcurrency } from "@/lib/concurrency";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import TimePicker from "@/components/TimePicker";
import { useAuth } from "@/context/AuthContext";
import WeatherStrip from "@/components/WeatherStrip";
import { toast } from "sonner";

// Time slots in 15-minute increments from 07:00 → 19:00. Used by the time
// picker on both the agent request form and admin scheduling flows.
const TIME_SLOTS = (() => {
    const out = [];
    for (let h = 7; h <= 19; h++) {
        for (const m of [0, 15, 30, 45]) {
            if (h === 19 && m > 0) break;
            out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
        }
    }
    return out;
})();
// Products that need a photo gallery: ENTRY AI is purely photo-based so it REQUIRES a min set.
// BASIC AI ("video met makelaar + AI invulling") can be shot on-site, so photos are OPTIONAL.
// Both share the same max cap because the AI fill workflow accepts up to 14 photos.
const REQUIRES_PHOTOS_PRODUCTS = new Set(["entry_ai"]);
const AI_FILL_PRODUCTS = new Set(["entry_ai", "basic_ai"]);
const MIN_AI_PHOTOS = 8;
const MAX_AI_PHOTOS = 50;

function pickTierPrice(product, photoCount) {
    if (!product.photo_tiers || product.photo_tiers.length === 0) return product.price;
    for (const t of product.photo_tiers) {
        if (photoCount >= t.min_photos && photoCount <= t.max_photos) return t.price;
    }
    return product.price;
}

export default function RequestForm() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, i18n } = useTranslation();
    const dfLocale = { nl, fr, es, en: enUS }[(i18n.language || "nl").slice(0, 2)] || nl;
    const isMM = user?.account_type === "marketing_manager";
    const isAdmin = user?.role === "admin";
    const isOnBehalf = isMM || isAdmin;
    const [managedAgents, setManagedAgents] = useState([]);
    const [onBehalfAgentId, setOnBehalfAgentId] = useState("");
    const [agentSearch, setAgentSearch] = useState("");
    const [products, setProducts] = useState([]);
    const [cart, setCart] = useState({}); // { product_id: { count, option_ids } } — count 1 or 0
    const [travel, setTravel] = useState({ distance_km: 0, travel_cost: 0, rate_per_km: 0.65, round_trip: true, base_address: null });
    const [travelLoading, setTravelLoading] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [blockedDates, setBlockedDates] = useState([]); // ["YYYY-MM-DD"]
    const [slots, setSlots] = useState([
        { date: null, time: "" },
        { date: null, time: "" },
        { date: null, time: "" },
    ]);
    const [form, setForm] = useState({
        address_street: "",
        address_city: "",
        address_zip: "",
        preferred_date: null,
        preferred_time: "",
        publication_date: null,
        email: user?.email || "",
        phone: user?.phone || "",
        description: "",
        property_link: "",
        photo_urls: [],
        document_urls: [],
    });
    const [uploading, setUploading] = useState(false);
    const [uploadingDocs, setUploadingDocs] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [weatherDays, setWeatherDays] = useState([]);
    const [weatherLoading, setWeatherLoading] = useState(false);

    useEffect(() => {
        const lang = (i18n.language || "nl").slice(0, 2).toLowerCase();
        api.get("/products", { params: { lang } }).then((r) => setProducts(r.data));
        if (isMM) {
            api.get("/marketing-manager/agents").then((r) => {
                // MMs are often makelaars themselves — always offer "myself" first
                const self = {
                    id: user.id,
                    name: user.name,
                    email: user.email,
                    phone: user.phone,
                    company: user.company,
                    logo_url: user.logo_url,
                    account_type: "marketing_manager",
                    _self: true,
                };
                const list = [self, ...(r.data || []).filter((a) => a.id !== user.id)];
                setManagedAgents(list);
                if (list.length === 1) setOnBehalfAgentId(list[0].id);
            }).catch(() => {});
        } else if (isAdmin) {
            // Admin can submit on behalf of ANY approved user — both agents
            // and marketing managers, because many MMs are themselves also
            // makelaars (they run their own listings alongside managing
            // other agents). We only strip out admin accounts.
            api.get("/admin/users?status=approved").then((r) => {
                const agents = (r.data || []).filter((u) => u.role !== "admin");
                setManagedAgents(agents);
            }).catch(() => {});
        }
    }, [isMM, isAdmin, i18n.language]);

    // When MM/Admin selects an agent, copy their data into the form so it pre-fills as if from that agent
    useEffect(() => {
        if (!isOnBehalf || !onBehalfAgentId) return;
        const a = managedAgents.find((x) => x.id === onBehalfAgentId);
        if (a) setForm((f) => ({ ...f, email: a.email || f.email, phone: a.phone || f.phone }));
    }, [isOnBehalf, onBehalfAgentId, managedAgents]);

    const needsTravel = Object.keys(cart).some((pid) => products.find((p) => p.id === pid)?.requires_on_site);

    // Debounced travel calculation when address + needsTravel
    useEffect(() => {
        if (!needsTravel || !form.address_street || !form.address_city || !form.address_zip) {
            setTravel((t) => ({ ...t, distance_km: 0, travel_cost: 0 }));
            return;
        }
        const id = setTimeout(async () => {
            setTravelLoading(true);
            try {
                const { data } = await api.post("/calculate-travel", {
                    address_street: form.address_street,
                    address_city: form.address_city,
                    address_zip: form.address_zip,
                });
                setTravel(data);
            } catch {}
            setTravelLoading(false);
        }, 700);
        return () => clearTimeout(id);
    }, [form.address_street, form.address_city, form.address_zip, needsTravel]);

    // Live 14-day weather forecast for the entered property address (debounced)
    useEffect(() => {
        const street = form.address_street?.trim();
        const city = form.address_city?.trim();
        if (!street || !city) {
            setWeatherDays([]);
            return;
        }
        const id = setTimeout(async () => {
            setWeatherLoading(true);
            try {
                const fullAddr = `${street}, ${form.address_zip || ""} ${city}`.trim();
                const { data } = await api.get("/weather/forecast", { params: { address: fullAddr, days: 14 } });
                setWeatherDays(data?.days || []);
            } catch {
                setWeatherDays([]);
            }
            setWeatherLoading(false);
        }, 700);
        return () => clearTimeout(id);
    }, [form.address_street, form.address_city, form.address_zip]);

    const toggleProduct = (pid) => {
        setCart((prev) => {
            const next = { ...prev };
            if (next[pid]) delete next[pid];
            else {
                // Default tier_id = first tier when product has photo_tiers
                const p = products.find((x) => x.id === pid);
                const defaultTier = p?.photo_tiers?.[0]?.id;
                next[pid] = { option_ids: [], ...(defaultTier ? { tier_id: defaultTier } : {}) };
            }
            return next;
        });
    };

    const setTier = (pid, tierId) => {
        setCart((prev) => {
            const entry = prev[pid] || { option_ids: [] };
            return { ...prev, [pid]: { ...entry, tier_id: tierId } };
        });
    };

    const toggleOption = (pid, oid) => {
        setCart((prev) => {
            const entry = prev[pid] || { option_ids: [] };
            const opts = entry.option_ids.includes(oid)
                ? entry.option_ids.filter((x) => x !== oid)
                : [...entry.option_ids, oid];
            return { ...prev, [pid]: { ...entry, option_ids: opts } };
        });
    };

    const tierPriceFor = (p, cartEntry) => {
        if (!p.photo_tiers || p.photo_tiers.length === 0) return p.price;
        if (cartEntry?.tier_id) {
            const t = p.photo_tiers.find((x) => x.id === cartEntry.tier_id);
            if (t) return t.price;
        }
        // Fallback to first tier (or photo-count derived)
        return p.photo_tiers[0].price;
    };

    const subtotalProducts = Object.entries(cart).reduce((acc, [pid, entry]) => {
        const p = products.find((x) => x.id === pid);
        if (!p) return acc;
        let v = tierPriceFor(p, entry);
        (entry.option_ids || []).forEach((oid) => {
            const o = p.options?.find((x) => x.id === oid);
            if (o) v += o.price;
        });
        return acc + v;
    }, 0);

    const total = subtotalProducts + (needsTravel ? (travel.travel_cost || 0) : 0);

    const onFileUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        const current = form.photo_urls.length;
        const hasAi = Object.keys(cart).some((pid) => AI_FILL_PRODUCTS.has(pid));
        if (hasAi && current + files.length > MAX_AI_PHOTOS) {
            const allowed = Math.max(0, MAX_AI_PHOTOS - current);
            toast.error(t("request.t_max_photos", { max: MAX_AI_PHOTOS, n: allowed }));
            if (allowed === 0) return;
            files.splice(allowed);
        }
        setUploading(true);
        try {
            // Upload 4 photos in parallel — ~4x faster than one-by-one,
            // especially noticeable on high-latency connections like Starlink.
            const urls = await runWithConcurrency(files, 4, async (f) => {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                return data.url;
            });
            setForm((f) => ({ ...f, photo_urls: [...f.photo_urls, ...urls] }));
            toast.success(t("request.t_photos_up", { n: files.length }));
        } catch {
            toast.error(t("request.t_up_fail"));
        }
        setUploading(false);
    };

    const removePhoto = (url) => setForm((f) => ({ ...f, photo_urls: f.photo_urls.filter((u) => u !== url) }));

    const onDocUpload = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;
        setUploadingDocs(true);
        try {
            const urls = await runWithConcurrency(files, 4, async (f) => {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                return { url: data.url, name: f.name };
            });
            setForm((f) => ({ ...f, document_urls: [...f.document_urls, ...urls] }));
            toast.success(t("request.t_docs_up", { n: files.length }));
        } catch {
            toast.error(t("request.t_up_fail"));
        }
        setUploadingDocs(false);
    };

    const removeDoc = (u) => setForm((f) => ({ ...f, document_urls: f.document_urls.filter((x) => x.url !== u) }));

    const hasAI = Object.keys(cart).some((pid) => AI_FILL_PRODUCTS.has(pid));
    const requiresPhotos = Object.keys(cart).some((pid) => REQUIRES_PHOTOS_PRODUCTS.has(pid));
    const aiPhotoShortage = requiresPhotos ? Math.max(0, MIN_AI_PHOTOS - form.photo_urls.length) : 0;

    const submit = async () => {
        if (isOnBehalf && !onBehalfAgentId) return toast.error(t("request.t_pick_agent"));
        if (Object.keys(cart).length === 0) return toast.error(t("request.t_pick_product"));
        if (!form.date_pending && (!form.preferred_date || !form.preferred_time)) return toast.error(t("request.t_pick_datetime"));
        if (!form.address_street || !form.address_city || !form.address_zip) return toast.error(t("request.t_fill_addr"));
        if (!form.email || !form.phone) return toast.error(t("request.t_fill_contact"));
        // Note: photo count is no longer required — makelaars kunnen ook foto's via WeTransfer / externe link doorsturen.
        if (!agreed) return toast.error(t("request.t_agree"));
        // Build preferred_slots for on-site products (slots 2 & 3 beyond the primary)
        const extraSlots = needsTravel
            ? slots.slice(1).filter((s) => s.date && s.time).map((s) => ({ date: format(s.date, "yyyy-MM-dd"), time: s.time }))
            : [];

        const items = Object.entries(cart).map(([product_id, entry]) => ({
            product_id,
            option_ids: entry.option_ids || [],
            ...(entry.tier_id ? { tier_id: entry.tier_id } : {}),
        }));
        setSubmitting(true);
        try {
            const { data } = await api.post("/requests", {
                items,
                address_street: form.address_street,
                address_city: form.address_city,
                address_zip: form.address_zip,
                preferred_date: form.date_pending ? null : (form.preferred_date ? format(form.preferred_date, "yyyy-MM-dd") : null),
                preferred_time: form.date_pending ? null : (form.preferred_time || null),
                preferred_slots: form.date_pending ? [] : extraSlots,
                date_pending: !!form.date_pending,
                publication_date: form.publication_date ? format(form.publication_date, "yyyy-MM-dd") : null,
                email: form.email,
                phone: form.phone,
                description: form.description,
                property_link: form.property_link || null,
                photo_urls: form.photo_urls,
                document_urls: form.document_urls.map((d) => typeof d === "string" ? d : d.url),
                on_behalf_of_agent_id: isOnBehalf ? onBehalfAgentId : null,
            });
            toast.success(t("request.t_sent"));
            navigate(`/projects/${data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || t("request.t_error"));
        }
        setSubmitting(false);
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="request-form-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-3">{t("request.eyebrow")}</div>
                    <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter">{t("request.title")}</h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Left: Form */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* MM / Admin agent picker */}
                        {isOnBehalf && (
                            <Section title={t("request.s0_title")} testid="section-on-behalf-of">
                                {managedAgents.length === 0 ? (
                                    <div className="text-sm text-zinc-500 italic">
                                        {isMM
                                            ? t("request.s0_none_mm")
                                            : t("request.s0_none_admin")}
                                    </div>
                                ) : (
                                    <>
                                        {isAdmin && managedAgents.length > 8 && (
                                            <Input
                                                placeholder={t("request.s0_search")}
                                                value={agentSearch}
                                                onChange={(e) => setAgentSearch(e.target.value)}
                                                className="bg-[#F4F4F5] border-zinc-200 mb-4 h-10 text-sm focus-visible:border-[#E5A93D]"
                                                data-testid="admin-agent-search"
                                            />
                                        )}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[480px] overflow-y-auto pr-1" data-testid="agent-picker-grid">
                                            {managedAgents
                                                .filter((a) => {
                                                    if (!agentSearch) return true;
                                                    const q = agentSearch.toLowerCase();
                                                    return (a.name || "").toLowerCase().includes(q) ||
                                                        (a.email || "").toLowerCase().includes(q) ||
                                                        (a.company || "").toLowerCase().includes(q);
                                                })
                                                .map((a) => {
                                                    const active = onBehalfAgentId === a.id;
                                                    return (
                                                        <button
                                                            key={a.id}
                                                            type="button"
                                                            onClick={() => setOnBehalfAgentId(a.id)}
                                                            className={`text-left p-4 rounded-md border transition ${active ? (isAdmin ? "bg-[#E5A93D]/10 border-[#E5A93D]" : "bg-[#7C3AED]/10 border-[#7C3AED]") : "bg-[#FFFFFF] border-zinc-200 hover:border-zinc-400"}`}
                                                            data-testid={`pick-agent-${a.id}`}
                                                        >
                                                            <div className="flex items-center gap-3">
                                                                {a.logo_url ? (
                                                                    <img src={a.logo_url.startsWith("http") ? a.logo_url : `${process.env.REACT_APP_BACKEND_URL}${a.logo_url}`} alt={a.name} className="h-10 w-10 rounded-sm object-contain bg-zinc-100" />
                                                                ) : (
                                                                    <div className={`h-10 w-10 rounded-sm border flex items-center justify-center text-sm font-bold ${isAdmin ? "bg-[#E5A93D]/15 border-[#E5A93D]/40 text-[#E5A93D]" : "bg-[#7C3AED]/15 border-[#7C3AED]/40 text-[#7C3AED]"}`}>{a.name?.charAt(0)}</div>
                                                                )}
                                                                <div className="min-w-0">
                                                                    <div className="text-sm font-medium text-zinc-950 truncate flex items-center gap-1.5">
                                                                        <span className="truncate">{a.name}</span>
                                                                        {a._self ? (
                                                                            <span
                                                                                className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#E5A93D]/15 border border-[#E5A93D]/50 text-[#B8860B]"
                                                                                data-testid="pick-agent-self-badge"
                                                                            >
                                                                                {t("request.self_badge")}
                                                                            </span>
                                                                        ) : a.account_type === "marketing_manager" && (
                                                                            <span
                                                                                className="shrink-0 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/40 text-[#7C3AED]"
                                                                                title="Marketing manager — kan ook zelf makelaar zijn"
                                                                            >
                                                                                MM
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-xs text-zinc-500 truncate">{a.company || a.email}</div>
                                                                </div>
                                                            </div>
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </>
                                )}
                                {onBehalfAgentId && (
                                    <p className="text-xs text-zinc-500 mt-3">{isAdmin ? t("request.s0_note_admin") : t("request.s0_note_mm")}</p>
                                )}
                            </Section>
                        )}

                        {/* Products */}
                        <Section title={t("request.s1_title")} testid="section-products">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {products.map((p) => {
                                    const active = !!cart[p.id];
                                    const cartEntry = cart[p.id];
                                    const displayPrice = active ? tierPriceFor(p, cartEntry) : (p.photo_tiers?.[0]?.price ?? p.price);
                                    return (
                                        <div key={p.id} className={`p-5 rounded-md border cursor-pointer transition-all ${active ? "bg-[#E5A93D]/10 border-[#E5A93D]" : "bg-[#FFFFFF] border-zinc-200 hover:border-zinc-400"}`} onClick={() => toggleProduct(p.id)} data-testid={`product-select-${p.id}`}>
                                            <div className="flex items-start justify-between mb-2">
                                                <div>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{p.name}</div>
                                                    <div className="font-display font-bold text-lg leading-tight mt-1">{p.tagline}</div>
                                                </div>
                                                <div className={`h-5 w-5 rounded-sm flex items-center justify-center flex-shrink-0 ${active ? "bg-[#E5A93D]" : "bg-transparent border border-zinc-300"}`}>
                                                    {active && <Check className="h-3.5 w-3.5 text-black" strokeWidth={3} />}
                                                </div>
                                            </div>
                                            <div className="text-[#E5A93D] font-display font-black text-xl">
                                                {p.price_from && !active && <span className="text-xs font-medium text-zinc-500 mr-1">{t("request.from")}</span>}€{displayPrice}
                                            </div>
                                            {p.photo_tiers && !active && (
                                                <div className="text-[10px] text-zinc-500 mt-1">
                                                    {p.photo_tiers.map((t) => `${t.label}: €${t.price}`).join(" · ")}
                                                </div>
                                            )}

                                            {/* Photo-tier picker (when product is active and has tiers) */}
                                            {active && p.photo_tiers?.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-zinc-200 space-y-2" onClick={(e) => e.stopPropagation()} data-testid={`tier-picker-${p.id}`}>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("request.tiers")}</div>
                                                    {p.photo_tiers.map((t) => {
                                                        const checked = cartEntry?.tier_id === t.id;
                                                        return (
                                                            <label key={t.id} className="flex items-center justify-between gap-2 cursor-pointer text-sm" data-testid={`tier-${p.id}-${t.id}`}>
                                                                <div className="flex items-center gap-2">
                                                                    <span className={`h-4 w-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${checked ? "border-[#E5A93D]" : "border-zinc-300"}`}>
                                                                        {checked && <span className="h-2 w-2 rounded-full bg-[#E5A93D]" />}
                                                                    </span>
                                                                    <input type="radio" className="hidden" checked={checked} onChange={() => setTier(p.id, t.id)} />
                                                                    <span className="text-zinc-800">{t.label}</span>
                                                                </div>
                                                                <span className="text-[#E5A93D] font-bold">€{t.price}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}

                                            {active && p.options?.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-zinc-200 space-y-2" onClick={(e) => e.stopPropagation()}>
                                                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{t("request.extras")}</div>
                                                    {p.options.map((o) => {
                                                        const checked = cart[p.id]?.option_ids?.includes(o.id);
                                                        return (
                                                            <label key={o.id} className="flex items-center justify-between gap-2 cursor-pointer text-sm" data-testid={`option-${p.id}-${o.id}`}>
                                                                <div className="flex items-center gap-2">
                                                                    <Checkbox checked={checked} onCheckedChange={() => toggleOption(p.id, o.id)} className="border-zinc-300 data-[state=checked]:bg-[#E5A93D] data-[state=checked]:border-[#E5A93D]" />
                                                                    <span className="text-zinc-600">{o.label}</span>
                                                                </div>
                                                                <span className="text-zinc-500">+€{o.price}</span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                            {p.requires_on_site && (
                                                <div className="mt-3 text-[10px] text-zinc-500 italic">
                                                    {t("request.travel_note")}
                                                </div>
                                            )}
                                            {p.delivery_time && (
                                                <div className="mt-2 text-[11px] text-[#E5A93D]/80" data-testid={`form-package-${p.id}-delivery`}>
                                                    {p.delivery_time}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </Section>

                        {/* Address */}
                        <Section title={t("request.s2_title")} testid="section-address">
                            {needsTravel && (
                                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-sm flex items-start gap-2" data-testid="travel-info">
                                    <AlertCircle className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                                    <div className="text-xs text-zinc-800 leading-relaxed">
                                        <span className="font-bold text-blue-600">{t("request.travel_auto")}</span> {t("request.travel_from")}{" "}
                                        {travel.base_address ? <>{t("request.travel_rate")} <strong>€{travel.rate_per_km}/km</strong> {travel.round_trip && t("request.travel_rt")}.</> : t("request.travel_wait")}
                                    </div>
                                </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="md:col-span-2">
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.street")}</Label>
                                    <AddressAutocomplete
                                        value={form.address_street}
                                        onChange={(v) => setForm((f) => ({ ...f, address_street: v }))}
                                        onSelect={({ street, postcode, city }) => setForm((f) => ({
                                            ...f,
                                            address_street: street || f.address_street,
                                            address_zip: postcode || f.address_zip,
                                            address_city: city || f.address_city,
                                        }))}
                                        placeholder="Kerkstraat 12"
                                        className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]"
                                        data-testid="address-street"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.zip")}</Label>
                                    <Input value={form.address_zip} onChange={(e) => setForm((f) => ({ ...f, address_zip: e.target.value }))} placeholder="9000" className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="address-zip" />
                                </div>
                                <div className="md:col-span-3">
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.city")}</Label>
                                    <Input value={form.address_city} onChange={(e) => setForm((f) => ({ ...f, address_city: e.target.value }))} placeholder="Gent" className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="address-city" />
                                </div>
                            </div>
                        </Section>

                        {/* Date/Time */}
                        <Section title={needsTravel ? t("request.s3_title_slots") : t("request.s3_title")} testid="section-datetime">
                            {/* Live 14-day weather for the entered property address */}
                            <div className="mb-5 bg-[#FFFFFF] border border-zinc-100 rounded-sm p-3 lg:p-4" data-testid="form-weather-section">
                                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold">{t("request.w2_title")}</div>
                                        <div className="text-xs text-zinc-500 mt-0.5">
                                            {form.address_street && form.address_city ? (
                                                <>{form.address_street}, {form.address_zip} {form.address_city}</>
                                            ) : (
                                                <span className="text-zinc-500 italic">{t("request.w2_fill")}</span>
                                            )}
                                        </div>
                                    </div>
                                    <span className="text-[10px] text-zinc-400">{t("request.w2_src")}</span>
                                </div>
                                {form.address_street && form.address_city ? (
                                    weatherLoading ? (
                                        <div className="text-xs text-zinc-500 text-center py-4">{t("request.w2_loading")}</div>
                                    ) : weatherDays.length > 0 ? (
                                        <WeatherStrip days={weatherDays} compact />
                                    ) : (
                                        <div className="text-xs text-zinc-500 text-center py-4">{t("request.w2_none")}</div>
                                    )
                                ) : null}
                            </div>

                            {/* No-date-yet checkbox */}
                            <label className="flex items-start gap-3 mb-5 p-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm cursor-pointer hover:border-[#E5A93D]/40 transition" data-testid="date-pending-toggle">
                                <input
                                    type="checkbox"
                                    checked={!!form.date_pending}
                                    onChange={(e) => setForm((f) => ({ ...f, date_pending: e.target.checked }))}
                                    className="mt-0.5 h-4 w-4 accent-[#E5A93D]"
                                    data-testid="date-pending-checkbox"
                                />
                                <div>
                                    <div className="text-sm font-medium text-zinc-950">{t("request.dp_title")}</div>
                                    <div className="text-xs text-zinc-500 mt-0.5">{t("request.dp_sub")}</div>
                                </div>
                            </label>
                            {form.date_pending ? (
                                <div className="text-sm text-zinc-500 italic px-3 py-4 border border-dashed border-zinc-200 rounded-sm" data-testid="date-pending-info">
                                    {t("request.dp_info")}
                                </div>
                            ) : (
                                <>
                                    {needsTravel && (
                                        <p className="text-xs text-zinc-500 mb-4">
                                            {t("request.slots_hint")}
                                        </p>
                                    )}
                                    {needsTravel ? (
                                        <div className="space-y-4">
                                    {[0, 1, 2].map((idx) => {
                                        const isPrimary = idx === 0;
                                        const slotDate = isPrimary ? form.preferred_date : slots[idx].date;
                                        const slotTime = isPrimary ? form.preferred_time : slots[idx].time;
                                        const setSlotDate = (d) => {
                                            if (isPrimary) setForm((f) => ({ ...f, preferred_date: d }));
                                            else setSlots((s) => s.map((x, i) => i === idx ? { ...x, date: d } : x));
                                        };
                                        const setSlotTime = (t) => {
                                            if (isPrimary) setForm((f) => ({ ...f, preferred_time: t }));
                                            else setSlots((s) => s.map((x, i) => i === idx ? { ...x, time: t } : x));
                                        };
                                        return (
                                            <div key={idx} className="grid grid-cols-1 md:grid-cols-[auto_1fr_1fr_auto] gap-3 items-end" data-testid={`slot-row-${idx}`}>
                                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#E5A93D] font-bold pb-3">{t("request.option_n", { n: idx + 1 })}{isPrimary && " *"}</div>
                                                <div>
                                                    {idx === 0 && <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.date")}</Label>}
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <button className="w-full h-11 px-3 flex items-center gap-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-left text-sm hover:border-zinc-400" data-testid={`slot-${idx}-date-trigger`}>
                                                                <CalIcon className="h-4 w-4 text-zinc-500" />
                                                                <span className={slotDate ? "text-zinc-950" : "text-zinc-500"}>
                                                                    {slotDate ? format(slotDate, "PPP", { locale: dfLocale }) : t("request.pick_date")}
                                                                </span>
                                                            </button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-zinc-200">
                                                            <Calendar
                                                                mode="single"
                                                                selected={slotDate}
                                                                onSelect={setSlotDate}
                                                                disabled={(d) => d < new Date(new Date().setHours(0,0,0,0)) || blockedDates.includes(format(d, "yyyy-MM-dd"))}
                                                                modifiers={{ blocked: blockedDates.map((ds) => new Date(ds + "T00:00:00")) }}
                                                                modifiersClassNames={{ blocked: "bg-red-500/20 text-red-600 line-through" }}
                                                                locale={dfLocale}
                                                                initialFocus
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                                <div>
                                                    {idx === 0 && <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.time")}</Label>}
                                                    <TimePicker
                                                        value={slotTime}
                                                        onChange={setSlotTime}
                                                        data-testid={`slot-${idx}-time-trigger`}
                                                    />
                                                </div>
                                                {!isPrimary && slotDate && (
                                                    <button
                                                        type="button"
                                                        onClick={() => { setSlots((s) => s.map((x, i) => i === idx ? { date: null, time: "" } : x)); }}
                                                        className="h-11 px-3 text-xs text-zinc-500 hover:text-red-600"
                                                        data-testid={`slot-${idx}-clear`}
                                                    >
                                                        {t("request.clear")}
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.date")}</Label>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <button className="w-full h-11 px-3 flex items-center gap-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm text-left text-sm hover:border-zinc-400" data-testid="date-trigger">
                                                    <CalIcon className="h-4 w-4 text-zinc-500" />
                                                    <span className={form.preferred_date ? "text-zinc-950" : "text-zinc-500"}>
                                                        {form.preferred_date ? format(form.preferred_date, "PPP", { locale: dfLocale }) : t("request.pick_date")}
                                                    </span>
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-zinc-200">
                                                <Calendar
                                                    mode="single"
                                                    selected={form.preferred_date}
                                                    onSelect={(d) => setForm((f) => ({ ...f, preferred_date: d }))}
                                                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                                                    locale={dfLocale}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.time")}</Label>
                                        <TimePicker
                                            value={form.preferred_time}
                                            onChange={(v) => setForm((f) => ({ ...f, preferred_time: v }))}
                                            data-testid="time-trigger"
                                        />
                                    </div>
                                </div>
                            )}
                                </>
                            )}
                        </Section>

                        {/* Contact */}
                        <Section title={t("request.s4_title")} testid="section-contact">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.email")}</Label>
                                    <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="contact-email" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.phone")}</Label>
                                    <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="contact-phone" />
                                </div>
                            </div>
                        </Section>

                        {/* Property info */}
                        <Section title={t("request.s5_title")} testid="section-property">
                            <div className="space-y-4">
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.desc")}</Label>
                                    <Textarea rows={5} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder={t("request.desc_ph")} className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="description-input" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.link")}</Label>
                                    <Input value={form.property_link} onChange={(e) => setForm((f) => ({ ...f, property_link: e.target.value }))} placeholder="https://..." className="bg-[#F4F4F5] border-zinc-200 rounded-sm focus-visible:border-[#E5A93D]" data-testid="property-link" />
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">
                                        {t("request.pub_date")}
                                    </Label>
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <button
                                                type="button"
                                                className="flex w-full items-center gap-2 bg-[#F4F4F5] border border-zinc-200 hover:border-[#E5A93D]/50 rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-[#E5A93D]"
                                                data-testid="publication-date-trigger"
                                            >
                                                <CalIcon className="h-3.5 w-3.5 text-[#E5A93D]" />
                                                <span className={form.publication_date ? "text-zinc-950" : "text-zinc-500"}>
                                                    {form.publication_date ? format(form.publication_date, "PPP", { locale: dfLocale }) : t("request.pub_ph")}
                                                </span>
                                                {form.publication_date && (
                                                    <span
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setForm((f) => ({ ...f, publication_date: null })); }}
                                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setForm((f) => ({ ...f, publication_date: null })); } }}
                                                        className="ml-auto text-zinc-500 hover:text-red-600 cursor-pointer"
                                                        data-testid="publication-date-clear"
                                                    >
                                                        ✕
                                                    </span>
                                                )}
                                            </button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0 bg-[#FFFFFF] border-zinc-200" align="start">
                                            <Calendar
                                                mode="single"
                                                selected={form.publication_date}
                                                onSelect={(d) => setForm((f) => ({ ...f, publication_date: d }))}
                                                disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                                                locale={dfLocale}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>
                                    <p className="text-[11px] text-zinc-500 mt-1.5">
                                        {t("request.pub_help")}
                                    </p>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <Label className="text-xs uppercase tracking-wider text-zinc-500">
                                            {t("request.photos")} {requiresPhotos && <span className="text-[#E5A93D]">*</span>}
                                            {hasAI && !requiresPhotos && <span className="text-zinc-500 normal-case ml-1">{t("request.optional")}</span>}
                                        </Label>
                                        {hasAI && (
                                            <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm ${requiresPhotos ? (form.photo_urls.length >= MIN_AI_PHOTOS ? "bg-emerald-500/20 text-emerald-600" : "bg-[#E5A93D]/20 text-[#E5A93D]") : "bg-zinc-100 text-zinc-600"}`} data-testid="photo-count-badge">
                                                {form.photo_urls.length} / {MAX_AI_PHOTOS}{requiresPhotos ? ` (min ${MIN_AI_PHOTOS})` : ""}
                                            </span>
                                        )}
                                    </div>
                                    {hasAI && (
                                        <div className="mb-3 p-3 bg-[#E5A93D]/10 border border-[#E5A93D]/30 rounded-sm flex items-start gap-2" data-testid="ai-photo-warning">
                                            <AlertCircle className="h-4 w-4 text-[#E5A93D] mt-0.5 flex-shrink-0" />
                                            <div className="text-xs text-zinc-800 leading-relaxed">
                                                {requiresPhotos ? (
                                                    <>
                                                        <span className="font-bold text-[#E5A93D]">{t("request.entry_title")}</span> {t("request.entry_body", { min: MIN_AI_PHOTOS, max: MAX_AI_PHOTOS })}
                                                        {(() => {
                                                            const tp = products.find((p) => cart[p.id] && p.photo_tiers?.length);
                                                            return tp ? (
                                                                <div className="mt-2 pt-2 border-t border-[#E5A93D]/20" data-testid="ai-tier-pricing">
                                                                    <strong className="text-zinc-950">{tp.name} {t("request.tariff")}</strong> {tp.photo_tiers.map((x) => `${x.label} = €${Number(x.price).toFixed(2).replace(".", ",")}`).join(" · ")}
                                                                </div>
                                                            ) : null;
                                                        })()}
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="font-bold text-[#E5A93D]">{t("request.basic_title")}</span> {t("request.basic_body", { max: MAX_AI_PHOTOS })}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                    <label className="flex items-center justify-center gap-2 h-24 border-2 border-dashed border-zinc-200 hover:border-[#E5A93D]/50 rounded-md cursor-pointer transition-colors" data-testid="photo-upload-area">
                                        <Upload className="h-4 w-4 text-zinc-500" />
                                        <span className="text-sm text-zinc-500">{uploading ? t("request.up_busy") : t("request.up_click")}</span>
                                        <input type="file" multiple accept="image/*" className="hidden" onChange={onFileUpload} />
                                    </label>
                                    {form.photo_urls.length > 0 && (
                                        <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2" data-testid="photo-grid">
                                            {form.photo_urls.map((u) => (
                                                <div key={u} className="relative aspect-square rounded-sm overflow-hidden border border-zinc-200">
                                                    <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="w-full h-full object-cover" />
                                                    <button onClick={() => removePhoto(u)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 flex items-center justify-center">
                                                        <X className="h-3 w-3 text-white" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">{t("request.docs")}</Label>
                                    <p className="text-[11px] text-zinc-500 mb-2">{t("request.docs_hint")}</p>
                                    <label className="flex items-center justify-center gap-2 h-20 border-2 border-dashed border-zinc-200 hover:border-[#E5A93D]/50 rounded-md cursor-pointer transition-colors" data-testid="doc-upload-area">
                                        <FileText className="h-4 w-4 text-zinc-500" />
                                        <span className="text-sm text-zinc-500">{uploadingDocs ? t("request.docs_busy") : t("request.docs_types")}</span>
                                        <input type="file" multiple accept=".pdf,.doc,.docx,image/*,application/pdf" className="hidden" onChange={onDocUpload} />
                                    </label>
                                    {form.document_urls.length > 0 && (
                                        <div className="mt-3 space-y-2" data-testid="doc-list">
                                            {form.document_urls.map((d) => (
                                                <div key={d.url} className="flex items-center gap-3 p-2 bg-[#F4F4F5] border border-zinc-200 rounded-sm">
                                                    <FileText className="h-4 w-4 text-[#E5A93D] flex-shrink-0" />
                                                    <span className="text-sm text-zinc-950 truncate flex-1">{d.name}</span>
                                                    <button onClick={() => removeDoc(d.url)} className="text-zinc-500 hover:text-red-600">
                                                        <X className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Section>
                    </div>

                    {/* Right: Summary */}
                    <div className="lg:col-span-1">
                        <div className="sticky top-24 bg-[#FFFFFF] border border-zinc-100 rounded-md p-6" data-testid="summary-sidebar">
                            <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-4">{t("request.summary")}</div>
                            {Object.keys(cart).length === 0 ? (
                                <div className="text-sm text-zinc-500 py-6 text-center">{t("request.pick_formula")}</div>
                            ) : (
                                <div className="space-y-3 mb-6">
                                    {Object.entries(cart).map(([pid, entry]) => {
                                        const p = products.find((x) => x.id === pid);
                                        if (!p) return null;
                                        const option_ids = entry.option_ids || [];
                                        const tierPrice = tierPriceFor(p, entry);
                                        const tierInfo = entry.tier_id ? p.photo_tiers?.find((t) => t.id === entry.tier_id) : null;
                                        return (
                                            <div key={pid} className="pb-3 border-b border-zinc-100 last:border-0">
                                                <div className="flex justify-between text-sm mb-1">
                                                    <span className="text-zinc-950 font-medium">{p.name}</span>
                                                    <span className="text-zinc-600">€{tierPrice}</span>
                                                </div>
                                                {tierInfo && (
                                                    <div className="text-[10px] text-zinc-500">{tierInfo.label}</div>
                                                )}
                                                {option_ids.map((oid) => {
                                                    const o = p.options?.find((x) => x.id === oid);
                                                    if (!o) return null;
                                                    return (
                                                        <div key={oid} className="flex justify-between text-xs text-zinc-500 pl-2">
                                                            <span>+ {o.label}</span>
                                                            <span>€{o.price}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <div className="flex items-end justify-between mb-6 pt-4 border-t border-zinc-200">
                                <span className="text-sm text-zinc-500">{t("request.subtotal")}</span>
                                <span className="font-medium text-zinc-950" data-testid="summary-subtotal">€{subtotalProducts.toFixed(2)}</span>
                            </div>
                            {needsTravel && (
                                <div className="flex items-center justify-between mb-4 pb-4 border-b border-zinc-200" data-testid="summary-travel">
                                    <div>
                                        <div className="text-sm text-zinc-500">{t("request.travel")}</div>
                                        <div className="text-[10px] text-zinc-500">
                                            {travelLoading ? t("request.calc") : travel.travel_cost > 0
                                                ? `${travel.distance_km} km × ${travel.round_trip ? t("request.rt2") : "1"} × €${travel.rate_per_km}/km`
                                                : travel.base_address ? t("request.fill_addr") : t("request.no_base")}
                                        </div>
                                    </div>
                                    <span className="font-medium text-zinc-950">€{(travel.travel_cost || 0).toFixed(2)}</span>
                                </div>
                            )}
                            <div className="flex items-end justify-between mb-4">
                                <span className="text-sm text-zinc-500">{t("request.total")}</span>
                                <span className="font-display font-black text-3xl text-[#E5A93D]" data-testid="summary-total">€{total.toFixed(2)}</span>
                            </div>
                            <label className="flex items-start gap-3 p-3 bg-[#F4F4F5] border border-zinc-200 rounded-sm cursor-pointer mb-4 hover:border-[#E5A93D]/50 transition-colors" data-testid="agree-checkbox-label">
                                <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5 border-zinc-300 data-[state=checked]:bg-[#E5A93D] data-[state=checked]:border-[#E5A93D]" data-testid="agree-checkbox" />
                                <span className="text-xs text-zinc-600 leading-relaxed">
                                    {t("request.agree")}
                                </span>
                            </label>
                            <Button
                                onClick={submit}
                                disabled={submitting || !agreed}
                                className="w-full h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold tracking-wide rounded-sm active:scale-[0.98] disabled:opacity-50"
                                data-testid="submit-request-btn"
                            >
                                {submitting ? t("request.submitting") : t("request.submit")}
                            </Button>
                            <p className="text-xs text-zinc-500 text-center mt-3">{t("request.after")}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Section({ title, testid, children }) {
    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-6 lg:p-8" data-testid={testid}>
            <h2 className="font-display font-bold text-xl tracking-tight mb-6">{title}</h2>
            {children}
        </motion.div>
    );
}
